import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "./ui/button";
import { 
  Loader2, RefreshCw, Table2, AlertCircle, Bell, X,
  ExternalLink, Maximize2, Minimize2, Volume2
} from "lucide-react";
import { apiClient } from "../App";
import { toast } from "sonner";

// Cache version - increment this when rendering logic changes to invalidate old caches
const CACHE_VERSION = "v2"; // v2: Added strength column highlighting

// Helper function to convert date from YYYY-MM-DD to DD-MM-YYYY
const formatDateCell = (cellValue) => {
  if (!cellValue || typeof cellValue !== 'string') return cellValue;
  
  // Match YYYY-MM-DD or YYYY/MM/DD format
  const dateMatch = cellValue.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
  if (dateMatch) {
    const [, year, month, day] = dateMatch;
    return `${day}-${month}-${year}`;
  }
  
  // Match YYYY-MM-DD with time (e.g., 2024-03-09 14:30:00)
  const dateTimeMatch = cellValue.match(/^(\d{4})[-/](\d{2})[-/](\d{2})\s+(.*)$/);
  if (dateTimeMatch) {
    const [, year, month, day, time] = dateTimeMatch;
    return `${day}-${month}-${year} ${time}`;
  }
  
  return cellValue;
};

// Better notification sound - encoded as base64 for reliability
const ALERT_SOUND = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQY4l+PcsoQJOZvv6b2DDjyh+PTDjQ9Gq///y5UQT7X//9GdE1jB//zXpRVfy//439mkGnHL//XdpBZ4zP/15aUZf8r/8OWnG4PJ/+7mph+HzP/u5qgijND/7eanI5DP/+3lpyaTz//t5Kcnl9D/7eOnKZrQ/+3jpyuc0P/t46YrnND/7eKmLJzP/+3ipSyc0P/t4qUsnc//7eGkLJ7P/+zhpC2ez//s4KMun8//7OCjLp/O/+zfoS+gz//r36Awoc7/6+CgMKLO/+vfnzGizv/r3p4yos3/696dM6PN/+vdnDSkzf/r3Zw0pM3/69ybNaXM/+rcmzamzP/q25o3p8z/6tqZOKjL/+rZmDmpzP/q2Zg5qcv/6diXOqrL/+nXljurzP/p1pU8rMv/6dWVPa3L/+nVlD2ty//p1JM+rsv/6NOSQLDKl5SXmJqXk42Id2hzgYaDWCo+Xn+iqYJQKT1eeaCsgFMpPmF8o7F/VClBYnyksmxWKUZjeaWzZ1soSnF0pbdiWy9Od3CmsF5eLVF8bamyV2E3VH9roLdRYjZbgmShvEphN16CYpy+R2Q8YYJdm8FHZT1kgliYxERlQWeCUpTIQWdFaoJNkMtBaUduh0iNzkJrTXSHQIrSQ25SeoM7h9hFb1V9ey+C30d0WIJQJ33lS3hchDwceOxQf2CFLQ9w81WFZ4cpBWr5W4pukxwAX/1jj3aZDgBT/m2VfaEFAEL+eJqEqQIAMf6FnousAAEf/pGgoq4AAQ7+np+irwABBv+loJ+sAAIB/6qhnKcAAwD/rqKZoAADAP+woJOYAAQA/7GfjY4ABQD/sp2GhQAGAP+ym3l7AAcA/7KYbXIACAD/sZViZwAJAP+wkVZfAAoA/66NSmgADAD/q4c+YQAOAP+ogDBQABEA/6N4ITkAFAD/nnQRJwAYAP+XahgUABwA/5BhHQkAIAD/hmIjAgAkAP96bycAAikA/21xKgACLwD/YXUAADQA/1h5AQA4AP9PfgMAOwD/R4QBAEIA/0CEAABKAP87eQAAUAD/OGoAAFYA/zRXAABcAP8wRQAAYgD/LTEAAGIA/ykdAABiAP8lDQAAYgD/IwAAAGIA/yIAAABiAP8iAAAAYgA=";

const AlgoSetupTab = ({ onClose, isFullScreen = false, onToggleFullScreen, highlightedCells = {} }) => {
  const [sheets, setSheets] = useState(() => {
    // Load cached sheets immediately from localStorage
    try {
      const cached = localStorage.getItem(`algo_sheets_cache_${CACHE_VERSION}`);
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [activeSheetIndex, setActiveSheetIndex] = useState(1);
  const [sheetData, setSheetData] = useState(() => {
    // Load cached data immediately from localStorage for instant display
    try {
      const cached = localStorage.getItem(`algo_data_cache_${CACHE_VERSION}_1`);
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(false); // Start false since we show cached data
  const [refreshing, setRefreshing] = useState(true); // Show refreshing on mount
  const [alerts, setAlerts] = useState([]);
  const [showAlertPopup, setShowAlertPopup] = useState(false);
  const [error, setError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [newCells, setNewCells] = useState({}); // Track newly updated cells for highlighting
  const pollIntervalRef = useRef(null);
  const audioRef = useRef(null);
  const isFirstLoadTodayRef = useRef(true); // Track if this is first load of the day
  const cachedDataRef = useRef({}); // Cache data per sheet for instant display
  const initialLoadDoneRef = useRef(false); // Track if initial load is complete

  // Initialize audio and check if first load today
  useEffect(() => {
    audioRef.current = new Audio(ALERT_SOUND);
    audioRef.current.volume = 0.5;
    
    // Check if this is first load of the day
    const today = new Date().toDateString();
    const lastLoadDate = localStorage.getItem('algo_signals_last_load_date');
    if (lastLoadDate !== today) {
      isFirstLoadTodayRef.current = true;
      localStorage.setItem('algo_signals_last_load_date', today);
    } else {
      isFirstLoadTodayRef.current = false;
    }
  }, []);

  // Play alert sound
  const playAlertSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Fetch available sheets - with caching
  const fetchSheets = useCallback(async () => {
    try {
      const response = await apiClient.get('/algo-setup/sheets');
      if (response.data.success) {
        const newSheets = response.data.sheets || [];
        setSheets(newSheets);
        // Cache sheets for instant future loads
        localStorage.setItem(`algo_sheets_cache_${CACHE_VERSION}`, JSON.stringify(newSheets));
        if (newSheets.length > 0 && !activeSheetIndex) {
          setActiveSheetIndex(newSheets[0].sheet_index);
        }
      }
    } catch (err) {
      console.error('Failed to fetch sheets:', err);
    }
  }, [activeSheetIndex]);

  // Fetch data for active sheet - ULTRA FAST with localStorage caching
  const fetchSheetData = useCallback(async (showLoader = true) => {
    if (!activeSheetIndex) return;
    
    const cacheKey = `algo_data_cache_${CACHE_VERSION}_${activeSheetIndex}`;
    
    // Try to load from localStorage cache first for INSTANT display
    if (!sheetData) {
      try {
        const localCache = localStorage.getItem(cacheKey);
        if (localCache) {
          const parsed = JSON.parse(localCache);
          setSheetData(parsed);
          setLoading(false);
        }
      } catch (e) {
        console.error('Cache read error:', e);
      }
    }
    
    // Show cached data from ref while fetching
    const cached = cachedDataRef.current[activeSheetIndex];
    if (cached) {
      setSheetData(cached);
      setLoading(false);
    } else if (!sheetData && showLoader) {
      setLoading(true);
    }
    
    setRefreshing(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/algo-setup/data/${activeSheetIndex}`);
      if (response.data.success) {
        if (response.data.headers?.length === 0 && response.data.data?.length === 0) {
          setError("No data found. Please ensure your Google Sheet is set to 'Anyone with the link can view'");
        }
        setSheetData(response.data);
        // Cache in memory ref
        cachedDataRef.current[activeSheetIndex] = response.data;
        // Cache in localStorage for instant future loads
        localStorage.setItem(cacheKey, JSON.stringify(response.data));
        initialLoadDoneRef.current = true;
      } else {
        setError(response.data.message || "Failed to load data");
        if (!cached && !sheetData) setSheetData(null);
      }
    } catch (err) {
      console.error('Failed to fetch sheet data:', err);
      setError("Failed to connect. Check if sheet URL is correct.");
      if (!cached && !sheetData) setSheetData(null);
    }
    
    setLoading(false);
    setRefreshing(false);
  }, [activeSheetIndex, sheetData]);

  // Check for changes and trigger alerts - ONLY for new rows AFTER first load
  const checkForChanges = useCallback(async () => {
    if (!activeSheetIndex) return;
    
    try {
      const response = await apiClient.get(`/algo-setup/data/${activeSheetIndex}`);
      if (!response.data.success || !response.data.data) return;
      
      const newData = response.data;
      const storageKey = `algo_data_${activeSheetIndex}`;
      
      // Get previous data from localStorage
      let previousData = null;
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) previousData = JSON.parse(stored);
      } catch (e) {
        console.error("Error reading stored data:", e);
      }
      
      // FIRST LOAD TODAY: Save data silently, no alerts
      if (isFirstLoadTodayRef.current) {
        console.log('AI Signals: First load of the day - saving baseline data, no alerts');
        localStorage.setItem(storageKey, JSON.stringify({
          data: newData.data,
          headers: newData.headers,
          rowCount: newData.data.length,
          timestamp: Date.now()
        }));
        setSheetData(newData);
        cachedDataRef.current[activeSheetIndex] = newData;
        isFirstLoadTodayRef.current = false; // Mark first load complete
        return;
      }
      
      // If no previous data exists, save current and skip alerting
      if (!previousData || !previousData.data) {
        localStorage.setItem(storageKey, JSON.stringify({
          data: newData.data,
          headers: newData.headers,
          rowCount: newData.data.length,
          timestamp: Date.now()
        }));
        setSheetData(newData);
        cachedDataRef.current[activeSheetIndex] = newData;
        return;
      }
      
      // ONLY alert for NEW ROWS (not for cell changes in existing rows)
      const changedCells = [];
      const previousRowCount = previousData.rowCount || previousData.data.length;
      
      // Get hidden columns from the sheet data
      const hiddenCols = newData.hidden_columns || [];
      
      // Check for new rows only
      if (newData.data.length > previousRowCount) {
        console.log(`AI Signals: Detected ${newData.data.length - previousRowCount} new row(s)`);
        for (let i = previousRowCount; i < newData.data.length; i++) {
          newData.data[i].forEach((cell, colIdx) => {
            // Skip hidden columns - only alert for visible columns
            if (hiddenCols.includes(colIdx)) {
              return;
            }
            if (cell && cell.trim()) {
              changedCells.push({
                rowIdx: i,
                colIdx,
                key: `${i}-${colIdx}`,
                header: newData.headers[colIdx] || `Column ${colIdx + 1}`,
                value: cell,
                isNewRow: true
              });
            }
          });
        }
      }
      
      // Update data
      setSheetData(newData);
      cachedDataRef.current[activeSheetIndex] = newData;
      
      // Save to localStorage with row count
      localStorage.setItem(storageKey, JSON.stringify({
        data: newData.data,
        headers: newData.headers,
        rowCount: newData.data.length,
        timestamp: Date.now()
      }));
      
      // Only alert if there are NEW ROWS (not cell changes)
      if (changedCells.length > 0) {
        // Clear previous highlights and set only new ones
        const newHighlights = {};
        changedCells.forEach(c => {
          newHighlights[c.key] = true;
        });
        setNewCells(newHighlights); // Replace, not merge
        
        // Create alerts
        const newAlerts = changedCells.map(change => ({
          message: `New Row: ${change.header.toUpperCase()}`,
          value: change.value,
          prevValue: change.prevValue
        }));
        
        setAlerts(prev => [...newAlerts, ...prev].slice(0, 20));
        setShowAlertPopup(true);
        
        // Play sound
        playAlertSound();
        
        // Show toast
        newAlerts.forEach(alert => {
          toast.success(alert.message, { duration: 5000, description: alert.value });
        });
        
        // Clear highlights after 2 minutes
        setTimeout(() => {
          setNewCells({});
        }, 120000);
      }
    } catch (err) {
      console.error('Failed to check for changes:', err);
    }
  }, [activeSheetIndex, playAlertSound]);

  // Open in popup window with ALL sheets and full functionality
  // Uses the dedicated React-based route /algo-setup-popout which renders the
  // SAME AlgoSetupTab component → fixes the long-standing "Unable to load data"
  // problem the old inline-HTML popup had (it duplicated the data-fetch logic
  // poorly).
  const openPopup = () => {
    const width = 1400;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const popupWindow = window.open(
      '/algo-setup-popout',
      'AlgoSetup_Popup',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popupWindow) {
      toast.error('Pop-up blocked. Please allow pop-ups for this site.');
    }
  };

  // ----- LEGACY inline-HTML popup builder kept temporarily for reference, no longer used -----
  // eslint-disable-next-line no-unused-vars
  const _legacyOpenPopup = () => {
    const width = 1400;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    const popupWindow = window.open(
      '',
      'AlgoSetup_Popup',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    
    if (popupWindow) {
      const token = localStorage.getItem('token');
      // Get the actual API URL - use current origin if env var not available
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      // Make sure it's a proper URL without trailing slash
      const apiUrl = backendUrl.replace(/\/$/, '');
      
      popupWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>AI Signals - AlphaTradeVission</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Inter', -apple-system, sans-serif; 
              background: #0a0e17; 
              color: white;
              min-height: 100vh;
            }
            .header { 
              display: flex; 
              justify-content: space-between; 
              align-items: center;
              padding: 12px 16px;
              border-bottom: 1px solid #1a2540;
              background: #111827;
              position: sticky;
              top: 0;
              z-index: 100;
            }
            .header-left { display: flex; align-items: center; gap: 16px; }
            .title { font-size: 16px; font-weight: bold; color: #22d3ee; }
            .tabs { display: flex; gap: 8px; }
            .tab {
              padding: 6px 12px;
              border-radius: 6px;
              font-size: 12px;
              cursor: pointer;
              border: 1px solid transparent;
              background: transparent;
              color: #6b7a99;
              transition: all 0.2s;
            }
            .tab:hover { color: white; background: #1a2540; }
            .tab.active { 
              background: rgba(34, 211, 238, 0.2); 
              color: #22d3ee; 
              border-color: rgba(34, 211, 238, 0.4);
            }
            .header-right { display: flex; align-items: center; gap: 8px; }
            .btn {
              background: #1a2540;
              border: 1px solid #2a3a5a;
              color: #22d3ee;
              padding: 6px 12px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 11px;
              display: flex;
              align-items: center;
              gap: 4px;
            }
            .btn:hover { background: #2a3a5a; }
            .btn.alert { background: rgba(239, 68, 68, 0.2); border-color: rgba(239, 68, 68, 0.4); color: #ef4444; }
            .badge {
              background: #ef4444;
              color: white;
              font-size: 9px;
              padding: 2px 5px;
              border-radius: 10px;
              margin-left: 4px;
            }
            .content { padding: 12px; height: calc(100vh - 60px); overflow: auto; }
            table { 
              width: 100%; 
              border-collapse: collapse;
              font-size: 12px;
            }
            th {
              background: linear-gradient(to bottom, #1a2540, #151d30);
              border: 1px solid #2a3a5a;
              padding: 10px 12px;
              text-align: left;
              color: #22d3ee;
              font-weight: bold;
              position: sticky;
              top: 0;
              z-index: 10;
            }
            td {
              border: 1px solid #2a3a5a;
              padding: 8px 12px;
              color: white;
            }
            tr:nth-child(even) { background: #0d1320; }
            tr:nth-child(odd) { background: #0a0e17; }
            tr:hover { background: rgba(26, 37, 64, 0.5); }
            .row-num {
              background: #151d30;
              color: #6b7a99;
              text-align: center;
              width: 40px;
              font-weight: 500;
            }
            .buy { background: rgba(34, 197, 94, 0.1); color: #22c55e; font-weight: 600; }
            .sell { background: rgba(239, 68, 68, 0.1); color: #ef4444; font-weight: 600; }
            .consecutive-match { background: rgba(234, 179, 8, 0.3); color: #fde047; font-weight: 700; border-color: rgba(234, 179, 8, 0.5); }
            .strength-green { background: rgba(34, 197, 94, 0.4); color: #bbf7d0; font-weight: 700; border-color: rgba(34, 197, 94, 0.5); }
            .strength-yellow { background: rgba(234, 179, 8, 0.4); color: #fef08a; font-weight: 700; border-color: rgba(234, 179, 8, 0.5); }
            .stats {
              margin-top: 12px;
              padding: 8px 12px;
              background: #111827;
              border-radius: 8px;
              font-size: 11px;
              color: #6b7a99;
              display: flex;
              justify-content: space-between;
            }
            .error {
              text-align: center;
              padding: 60px 20px;
              color: #6b7a99;
            }
            .error-icon { font-size: 48px; margin-bottom: 16px; }
            .loading { 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              height: 300px; 
              color: #22d3ee;
            }
            .alert-popup {
              position: fixed;
              top: 70px;
              right: 16px;
              width: 350px;
              max-height: 400px;
              background: #111827;
              border: 1px solid rgba(239, 68, 68, 0.4);
              border-radius: 12px;
              box-shadow: 0 20px 50px rgba(0,0,0,0.5);
              z-index: 200;
              overflow: hidden;
            }
            .alert-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 10px 12px;
              background: rgba(239, 68, 68, 0.1);
              border-bottom: 1px solid #1a2540;
            }
            .alert-title { font-size: 13px; font-weight: bold; color: #ef4444; }
            .alert-close { background: none; border: none; color: #6b7a99; cursor: pointer; font-size: 18px; }
            .alert-list { max-height: 300px; overflow-y: auto; padding: 8px; }
            .alert-item {
              padding: 10px;
              background: #0a0e17;
              border: 1px solid rgba(239, 68, 68, 0.3);
              border-radius: 8px;
              margin-bottom: 8px;
            }
            .alert-msg { font-size: 13px; font-weight: 600; color: white; }
            .alert-value { font-size: 11px; color: #6b7a99; margin-top: 4px; }
            .alert-value span { color: #22d3ee; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-left">
              <div class="title">📊 AI Signals</div>
              <div class="tabs" id="tabs"></div>
            </div>
            <div class="header-right">
              <button class="btn alert" id="alertBtn" style="display:none">
                🔔 Alerts <span class="badge" id="alertCount">0</span>
              </button>
              <button class="btn" onclick="toggleSound()">
                🔊 Sound: <span id="soundStatus">ON</span>
              </button>
              <button class="btn" onclick="refreshData()">⟳ Refresh</button>
            </div>
          </div>
          <div class="content" id="content">
            <div class="loading">Loading...</div>
          </div>
          <div class="alert-popup" id="alertPopup" style="display:none">
            <div class="alert-header">
              <div class="alert-title">🚨 Algo Alerts</div>
              <button class="alert-close" onclick="closeAlerts()">×</button>
            </div>
            <div class="alert-list" id="alertList"></div>
          </div>
          
          <script>
            const API_URL = '${apiUrl}';
            const TOKEN = '${token}';
            let sheets = [];
            let activeSheet = null;
            let alerts = [];
            let previousData = {};
            let previousRowCount = {}; // Track row counts for new row detection
            let soundEnabled = true;
            let isFirstLoadToday = true; // Don't alert on first load
            
            // Alert sound
            const alertSound = new Audio('${ALERT_SOUND}');
            alertSound.volume = 0.5;
            
            // Date format converter: YYYY-MM-DD to DD-MM-YYYY
            function formatDate(cellValue) {
              if (!cellValue || typeof cellValue !== 'string') return cellValue;
              const dateMatch = cellValue.match(/^(\\d{4})[-/](\\d{2})[-/](\\d{2})$/);
              if (dateMatch) {
                return dateMatch[3] + '-' + dateMatch[2] + '-' + dateMatch[1];
              }
              const dateTimeMatch = cellValue.match(/^(\\d{4})[-/](\\d{2})[-/](\\d{2})\\s+(.*)$/);
              if (dateTimeMatch) {
                return dateTimeMatch[3] + '-' + dateTimeMatch[2] + '-' + dateTimeMatch[1] + ' ' + dateTimeMatch[4];
              }
              return cellValue;
            }
            
            function playSound() {
              if (soundEnabled) {
                alertSound.currentTime = 0;
                alertSound.play().catch(() => {});
              }
            }
            
            function toggleSound() {
              soundEnabled = !soundEnabled;
              document.getElementById('soundStatus').textContent = soundEnabled ? 'ON' : 'OFF';
            }
            
            async function fetchSheets() {
              try {
                const response = await fetch(API_URL + '/api/algo-setup/sheets', {
                  headers: { 'Authorization': 'Bearer ' + TOKEN }
                });
                const data = await response.json();
                if (data.success) {
                  sheets = data.sheets;
                  renderTabs();
                  if (sheets.length > 0 && !activeSheet) {
                    selectSheet(sheets[0].sheet_index);
                  }
                }
              } catch (err) {
                console.error('Failed to fetch sheets:', err);
              }
            }
            
            function renderTabs() {
              const tabsEl = document.getElementById('tabs');
              tabsEl.innerHTML = sheets.map(s => 
                '<button class="tab ' + (activeSheet === s.sheet_index ? 'active' : '') + '" onclick="selectSheet(' + s.sheet_index + ')">' + 
                  (s.tab_name || 'Data ' + s.sheet_index) + 
                '</button>'
              ).join('');
            }
            
            function selectSheet(index) {
              activeSheet = index;
              renderTabs();
              fetchData();
            }
            
            async function fetchData() {
              if (!activeSheet) return;
              
              document.getElementById('content').innerHTML = '<div class="loading">Loading...</div>';
              
              try {
                const response = await fetch(API_URL + '/api/algo-setup/data/' + activeSheet, {
                  headers: { 'Authorization': 'Bearer ' + TOKEN }
                });
                const data = await response.json();
                renderData(data);
                checkForChanges(data);
              } catch (err) {
                document.getElementById('content').innerHTML = '<div class="error"><div class="error-icon">⚠️</div><div>Failed to load data</div></div>';
              }
            }
            
            function checkForChanges(data) {
              if (!data.success || !data.data) return;
              
              const sheetKey = 'sheet_' + activeSheet;
              const prevRowCount = previousRowCount[sheetKey] || 0;
              const newRowCount = data.data.length;
              
              // Get hidden columns for this sheet
              const hiddenCols = data.hidden_columns || [];
              
              // First load - save baseline, no alerts
              if (isFirstLoadToday || prevRowCount === 0) {
                previousData[sheetKey] = data.data || [];
                previousRowCount[sheetKey] = newRowCount;
                isFirstLoadToday = false;
                console.log('Popup: First load - saving baseline, no alerts');
                return;
              }
              
              // Only alert for NEW ROWS
              const newAlerts = [];
              const headers = data.headers || [];
              
              if (newRowCount > prevRowCount) {
                console.log('Popup: Detected ' + (newRowCount - prevRowCount) + ' new row(s)');
                for (let i = prevRowCount; i < newRowCount; i++) {
                  const row = data.data[i];
                  row.forEach((cell, idx) => {
                    // Skip hidden columns - only alert for visible columns
                    if (hiddenCols.includes(idx)) {
                      return;
                    }
                    if (cell && cell.trim() && idx < headers.length) {
                      newAlerts.push({
                        message: 'New Row: ' + headers[idx].toUpperCase(),
                        value: cell
                      });
                    }
                  });
                }
              }
              
              if (newAlerts.length > 0) {
                alerts = [...newAlerts, ...alerts].slice(0, 20);
                showAlertPopup();
                playSound();
              }
              
              // Update tracking data
              previousData[sheetKey] = data.data || [];
              previousRowCount[sheetKey] = newRowCount;
            }
            
            function showAlertPopup() {
              const popup = document.getElementById('alertPopup');
              const list = document.getElementById('alertList');
              const btn = document.getElementById('alertBtn');
              const count = document.getElementById('alertCount');
              
              popup.style.display = 'block';
              btn.style.display = 'flex';
              count.textContent = alerts.length;
              
              list.innerHTML = alerts.map(a => 
                '<div class="alert-item">' +
                  '<div class="alert-msg">' + a.message + '</div>' +
                  '<div class="alert-value">Value: <span>' + a.value + '</span></div>' +
                '</div>'
              ).join('');
            }
            
            function closeAlerts() {
              document.getElementById('alertPopup').style.display = 'none';
            }
            
            function renderData(data) {
              const content = document.getElementById('content');
              
              if (!data.success || !data.headers || data.headers.length === 0) {
                content.innerHTML = '<div class="error"><div class="error-icon">📊</div><div>No data available</div><button class="btn" style="margin-top:12px" onclick="refreshData()">⟳ Retry</button></div>';
                return;
              }
              
              const hiddenCols = data.hidden_columns || [];
              const showRowNums = data.show_row_numbers !== false;
              const rows = data.data || [];
              // Reverse rows so latest appears at top
              const reversedRows = [...rows].reverse();
              
              let html = '<table><thead><tr>';
              if (showRowNums) html += '<th class="row-num">#</th>';
              
              data.headers.forEach((h, idx) => {
                if (!hiddenCols.includes(idx)) {
                  html += '<th>' + (h || 'Column ' + (idx+1)) + '</th>';
                }
              });
              html += '</tr></thead><tbody>';
              
              reversedRows.forEach((row, rowIdx) => {
                // Check for consecutive match in first visible column
                const visibleRow = row.filter((_, idx) => !hiddenCols.includes(idx));
                const firstColValue = visibleRow[0] || '';
                const prevRow = rowIdx > 0 ? reversedRows[rowIdx - 1].filter((_, idx) => !hiddenCols.includes(idx)) : null;
                const nextRow = rowIdx < reversedRows.length - 1 ? reversedRows[rowIdx + 1].filter((_, idx) => !hiddenCols.includes(idx)) : null;
                const prevFirstCol = prevRow ? (prevRow[0] || '') : null;
                const nextFirstCol = nextRow ? (nextRow[0] || '') : null;
                const isConsecutiveMatch = (prevFirstCol !== null && firstColValue === prevFirstCol) || 
                                           (nextFirstCol !== null && firstColValue === nextFirstCol);
                
                html += '<tr>';
                // Show original row number (from sheet): total rows - current index
                if (showRowNums) html += '<td class="row-num">' + (reversedRows.length - rowIdx) + '</td>';
                let visibleCellIdx = 0;
                row.forEach((cell, cellIdx) => {
                  if (!hiddenCols.includes(cellIdx)) {
                    let cls = '';
                    const headerName = data.headers[cellIdx]?.toLowerCase() || '';
                    const isStrengthCol = headerName.includes('aggresive') || headerName.includes('aggressive') || headerName.includes('strength');
                    
                    // Check for strength column highlighting first
                    if (isStrengthCol && cell) {
                      const numValue = parseFloat(cell);
                      if (!isNaN(numValue)) {
                        if (numValue > 15) {
                          cls = 'strength-green';
                        } else if (numValue > 10) {
                          cls = 'strength-yellow';
                        }
                      }
                    }
                    
                    // If not strength highlighted, check other conditions
                    if (!cls) {
                      // First visible column with consecutive match
                      if (visibleCellIdx === 0 && isConsecutiveMatch) {
                        cls = 'consecutive-match';
                      } else if (cell.toLowerCase().includes('buy')) {
                        cls = 'buy';
                      } else if (cell.toLowerCase().includes('sell')) {
                        cls = 'sell';
                      }
                    }
                    // Format date from YYYY-MM-DD to DD-MM-YYYY
                    const displayValue = formatDate(cell);
                    html += '<td class="' + cls + '">' + displayValue + '</td>';
                    visibleCellIdx++;
                  }
                });
                html += '</tr>';
              });
              
              html += '</tbody></table>';
              html += '<div class="stats"><span>Rows: ' + data.total_rows + ' | Columns: ' + data.total_columns + '</span><span>Auto-refresh: 30s | Last: ' + new Date().toLocaleTimeString() + '</span></div>';
              
              content.innerHTML = html;
            }
            
            function refreshData() {
              fetchData();
            }
            
            // Initialize
            fetchSheets();
            
            // Auto-refresh every 30 seconds
            setInterval(() => {
              fetchData();
            }, 30000);
          </script>
        </body>
        </html>
      `);
      popupWindow.document.close();
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchSheets();
  }, [fetchSheets]);

  // Fetch data when active sheet changes
  useEffect(() => {
    if (activeSheetIndex) {
      fetchSheetData(true);
    }
  }, [activeSheetIndex, fetchSheetData]);

  // Set up polling every 30 seconds
  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      checkForChanges();
    }, 2000);

    pollIntervalRef.current = setInterval(() => {
      checkForChanges();
    }, 30000);

    return () => {
      clearTimeout(initialTimeout);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [checkForChanges]);

  // Get hidden columns for active sheet
  const activeSheet = sheets.find(s => s.sheet_index === activeSheetIndex);
  const hiddenColumns = activeSheet?.hidden_columns || [];
  const showRowNumbers = activeSheet?.show_row_numbers !== false;

  // Filter headers and data based on hidden columns
  const visibleHeaders = sheetData?.headers?.filter((_, idx) => !hiddenColumns.includes(idx)) || [];
  // Reverse data so latest rows appear at top (newest first)
  const visibleData = sheetData?.data?.map(row => 
    row.filter((_, idx) => !hiddenColumns.includes(idx))
  ).reverse() || [];

  return (
    <div className={`h-full flex flex-col bg-[#0a0e17] ${isFullScreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#1a2540] bg-[#111827]">
        <div className="flex items-center gap-3">
          <Table2 className="w-5 h-5 text-cyan-400" />
          <h2 className="text-sm font-bold text-white">AI Signals</h2>
          
          {/* Sheet Tabs */}
          <div className="flex items-center gap-1 ml-4">
            {sheets.length === 0 && (
              <span className="text-[10px] text-[#6b7a99]">Not configured</span>
            )}
            {sheets.map(sheet => (
              <button
                key={sheet.sheet_index}
                onClick={() => setActiveSheetIndex(sheet.sheet_index)}
                className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                  activeSheetIndex === sheet.sheet_index
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40'
                    : 'text-[#6b7a99] hover:text-white hover:bg-[#1a2540]'
                }`}
              >
                {sheet.tab_name || `Data ${sheet.sheet_index}`}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Sound toggle */}
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-1.5 rounded-lg transition-all ${
              soundEnabled 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-[#1a2540] text-[#6b7a99]'
            }`}
            title={soundEnabled ? 'Sound ON' : 'Sound OFF'}
          >
            <Volume2 className="w-4 h-4" />
          </button>
          
          {/* Alerts indicator */}
          {alerts.length > 0 && (
            <button
              onClick={() => setShowAlertPopup(true)}
              className="relative p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
            >
              <Bell className="w-4 h-4" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center">
                {alerts.length}
              </span>
            </button>
          )}
          
          {/* Refresh button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fetchSheetData(false)}
            disabled={refreshing}
            className="h-7 px-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="text-[10px]">Refresh</span>
          </Button>
          
          {/* Pop-out button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={openPopup}
            className="h-7 px-2"
            title="Open in new window"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1" />
            <span className="text-[10px]">Pop-out</span>
          </Button>
          
          {/* Fullscreen toggle */}
          {onToggleFullScreen && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onToggleFullScreen}
              className="h-7 px-2"
              title={isFullScreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {isFullScreen ? (
                <Minimize2 className="w-3.5 h-3.5" />
              ) : (
                <Maximize2 className="w-3.5 h-3.5" />
              )}
            </Button>
          )}
          
          {/* Close button */}
          <button onClick={onClose} className="p-1 text-[#6b7a99] hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-2">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mb-3" />
            <p className="text-[#6b7a99] text-xs">Loading sheet data...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <AlertCircle className="w-12 h-12 text-[#4a5a7a] mb-4" />
            <p className="text-[#6b7a99] text-sm mb-4">Unable to load data</p>
            <Button
              onClick={() => fetchSheetData(true)}
              size="sm"
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Retry
            </Button>
          </div>
        ) : !sheetData || visibleHeaders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Table2 className="w-12 h-12 text-[#4a5a7a] mb-4" />
            <p className="text-[#6b7a99] text-sm mb-4">
              {sheets.length === 0 
                ? "No data configured. Contact admin."
                : "No data available."}
            </p>
            <Button
              onClick={() => fetchSheetData(true)}
              size="sm"
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Retry
            </Button>
          </div>
        ) : (
          <div className="overflow-auto h-full">
            {/* Excel-like Table */}
            <table className="w-full border-collapse min-w-max">
              {/* Header Row */}
              <thead className="sticky top-0 z-10">
                <tr>
                  {showRowNumbers && (
                    <th className="bg-[#1a2540] border border-[#2a3a5a] px-2 py-1.5 text-[10px] font-bold text-[#6b7a99] text-center w-10 sticky left-0 z-20">
                      #
                    </th>
                  )}
                  {visibleHeaders.map((header, colIdx) => (
                    <th
                      key={colIdx}
                      className="bg-gradient-to-b from-[#1a2540] to-[#151d30] border border-[#2a3a5a] px-3 py-2 text-xs font-bold text-cyan-400 text-left whitespace-nowrap min-w-[100px]"
                    >
                      {header || `Column ${colIdx + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              
              {/* Data Rows */}
              <tbody>
                {visibleData.map((row, rowIdx) => {
                  // Check if first column has same value as previous row (for consecutive highlight)
                  const firstColValue = row[0] || '';
                  const prevFirstColValue = rowIdx > 0 ? (visibleData[rowIdx - 1]?.[0] || '') : null;
                  const nextFirstColValue = rowIdx < visibleData.length - 1 ? (visibleData[rowIdx + 1]?.[0] || '') : null;
                  const isConsecutiveMatch = (prevFirstColValue !== null && firstColValue === prevFirstColValue) || 
                                             (nextFirstColValue !== null && firstColValue === nextFirstColValue);
                  
                  return (
                    <tr 
                      key={rowIdx}
                      className={`hover:bg-[#1a2540]/50 ${rowIdx % 2 === 0 ? 'bg-[#0d1320]' : 'bg-[#0a0e17]'}`}
                    >
                      {showRowNumbers && (
                        <td className="bg-[#151d30] border border-[#2a3a5a] px-2 py-1.5 text-[10px] font-medium text-[#6b7a99] text-center sticky left-0">
                          {visibleData.length - rowIdx}
                        </td>
                      )}
                      {row.map((cell, cellIdx) => {
                        // Get original column index for hidden columns tracking
                        const origColIdx = hiddenColumns.length > 0 
                          ? sheetData?.headers?.findIndex((h, i) => !hiddenColumns.includes(i) && visibleHeaders[cellIdx] === h)
                          : cellIdx;
                        const cellKey = `${rowIdx}-${origColIdx !== -1 ? origColIdx : cellIdx}`;
                        const isNewCell = newCells[cellKey] || (highlightedCells[activeSheetIndex] && highlightedCells[activeSheetIndex].includes(cellKey));
                        
                        // Highlight first column if consecutive rows have same value
                        const isFirstColConsecutive = cellIdx === 0 && isConsecutiveMatch;
                        
                        // Check if this is the "Aggresive Strength" column
                        const columnHeader = visibleHeaders[cellIdx]?.toLowerCase() || '';
                        const isStrengthColumn = columnHeader.includes('aggresive') || columnHeader.includes('aggressive') || columnHeader.includes('strength');
                        
                        // Get numeric value for strength column highlighting
                        let strengthHighlight = null;
                        if (isStrengthColumn && cell) {
                          const numValue = parseFloat(cell);
                          if (!isNaN(numValue)) {
                            if (numValue > 15) {
                              strengthHighlight = 'green'; // > 15 = green background
                            } else if (numValue > 10) {
                              strengthHighlight = 'yellow'; // > 10 = yellow background
                            }
                          }
                        }
                        
                        // Format date cells from YYYY-MM-DD to DD-MM-YYYY
                        const displayValue = formatDateCell(cell);
                        
                        return (
                          <td
                            key={cellIdx}
                            className={`border border-[#2a3a5a] px-3 py-1.5 text-xs whitespace-nowrap transition-all duration-500 ${
                              isNewCell
                                ? 'bg-green-500/30 text-green-300 font-bold animate-pulse border-green-500/50'
                                : strengthHighlight === 'green'
                                ? 'bg-green-500/40 text-green-200 font-bold border-green-500/50'
                                : strengthHighlight === 'yellow'
                                ? 'bg-yellow-500/40 text-yellow-200 font-bold border-yellow-500/50'
                                : isFirstColConsecutive
                                ? 'bg-yellow-500/30 text-yellow-300 font-bold border-yellow-500/50'
                                : cell.toLowerCase().includes('buy') 
                                ? 'bg-green-500/10 text-green-400 font-semibold'
                                : cell.toLowerCase().includes('sell')
                                ? 'bg-red-500/10 text-red-400 font-semibold'
                                : 'text-white'
                            }`}
                          >
                            {displayValue}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Footer Stats */}
            <div className="flex items-center justify-between mt-2 px-2 py-1.5 bg-[#111827] rounded-lg text-[10px] text-[#6b7a99] sticky bottom-0">
              <span>Rows: {sheetData.total_rows} | Columns: {visibleHeaders.length}</span>
              <span className="flex items-center gap-2">
                {refreshing && <RefreshCw className="w-3 h-3 animate-spin text-cyan-400" />}
                Auto-refresh: 30s | Last: {new Date(sheetData.fetched_at).toLocaleTimeString()}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Alert Popup */}
      {showAlertPopup && alerts.length > 0 && (
        <div className="absolute top-14 right-4 w-80 bg-[#111827] border border-red-500/40 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between p-3 border-b border-[#1a2540] bg-red-500/10">
            <h3 className="text-sm font-bold text-red-400 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Algo Alerts ({alerts.length})
            </h3>
            <button onClick={() => setShowAlertPopup(false)} className="text-[#6b7a99] hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto p-2 space-y-2">
            {alerts.map((alert, idx) => (
              <div key={idx} className="p-3 bg-[#0a0e17] border border-red-500/30 rounded-lg">
                <p className="text-sm font-semibold text-white">{alert.message}</p>
                <p className="text-xs text-[#6b7a99] mt-1">
                  Value: <span className="text-cyan-400">{alert.value}</span>
                </p>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-[#1a2540]">
            <Button
              onClick={() => { setAlerts([]); setShowAlertPopup(false); }}
              variant="outline"
              size="sm"
              className="w-full text-xs"
            >
              Clear All Alerts
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlgoSetupTab;
